import { ChangeDetectorRef, Component, computed, effect, EventEmitter, Input, Output, signal } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ColumnDefinition } from '../../models/column-definition';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { FormsModule } from '@angular/forms';

export interface TableColumn {
  key: string;
  label: string;
  visible: boolean;
  required?: boolean;
}

export interface ColumnConfig {
  columns: ColumnDefinition[];
  displayedColumns: (string | undefined)[];
}

export interface ColumnManagerDialogData {
  tableId: string;
  columns: ColumnDefinition[];
  defaultColumns: ColumnDefinition[];
  autoUpdate: boolean;
}

@Component({
  selector: 'app-column-manager',
  imports: [MatIconModule, MatCheckboxModule, DragDropModule, MatButtonModule, MatFormFieldModule, MatInputModule, ScrollingModule, FormsModule],
  templateUrl: './column-manager.component.html',
  styleUrl: './column-manager.component.scss'
})

export class ColumnManagerComponent {
  @Input() inputColumns: ColumnDefinition[] = [];
  @Input() inputDefaultColumns: ColumnDefinition[] = [];
  @Output() columnsChange = new EventEmitter<ColumnConfig>();
  @Output() close = new EventEmitter<void>();

  managedColumns = signal<ColumnDefinition[]>([]);
  searchTerm = signal<string>('');
  filteredColumns = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) {
      return this.managedColumns();
    }
    return this.managedColumns().filter(col =>
      col.header.toLowerCase().includes(term)
    );
  });
  originalColumns: ColumnDefinition[] = [];
  defaultColumns: ColumnDefinition[] = [];

  ngOnInit() {
    this.originalColumns = JSON.parse(JSON.stringify(this.inputColumns));
    this.defaultColumns = JSON.parse(JSON.stringify(this.inputDefaultColumns));
    this.managedColumns.set(JSON.parse(JSON.stringify(this.inputColumns)));
  }

  onSelectAll() {
    this.managedColumns().forEach(col => {
      if (!col.required) {
        col.visible = true;
      }
    });
  }

  onDeselectAll() {
    this.managedColumns().forEach(col => {
      if (!col.required) {
        col.visible = false;
      }
    });
  }

  onReset() {
    this.managedColumns.set(JSON.parse(JSON.stringify(this.originalColumns)));
    this.searchTerm.set('');
  }

  onResetToDefault() {
    this.managedColumns.set(JSON.parse(JSON.stringify(this.defaultColumns)));
    this.searchTerm.set('');
  }

  onSave() {
    this.emitChanges();
    this.onClose();
  }

  onClose() {
    this.close.emit();
  }

  clearSearch(): void {
    this.searchTerm.set('');
  }

  onCheckboxChange(column: ColumnDefinition, event: any): void {
    column.visible = event.checked;
  }

  onDrop(event: CdkDragDrop<TableColumn[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    if (event.currentIndex === 0) {
      return;
    }

    // If we're not filtering, simple reorder
    if (!this.searchTerm().trim()) {
      moveItemInArray(this.managedColumns(), event.previousIndex, event.currentIndex);
    } else {
      // If search, handle filter
      this.handleFilteredDrop(event);
    }

    this.managedColumns.set([...this.managedColumns()]);
  }

  private handleFilteredDrop(event: CdkDragDrop<TableColumn[]>): void {
    //Om vi har ett sökfilter så stämmer inte index på managerade kolumnerna vid drop, detta löser det.
    const filtered = this.filteredColumns();
    const draggedColumn = filtered[event.previousIndex];
    const targetColumn = filtered[event.currentIndex];

    const draggedIndex = this.managedColumns().findIndex(col => col.columnDef === draggedColumn.columnDef);
    const targetIndex = this.managedColumns().findIndex(col => col.columnDef === targetColumn.columnDef);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      const [removed] = this.managedColumns().splice(draggedIndex, 1);

      const newTargetIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
      this.managedColumns().splice(newTargetIndex, 0, removed);
    }
  }

  private emitChanges() {
    const displayedColumns = this.managedColumns()
      .filter(col => col.visible)
      .map(col => col.columnDef);

    this.columnsChange.emit({
      columns: [...this.managedColumns()],
      displayedColumns
    });
  }


}
